import React, { useEffect, useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { updateCaseStudies, getCaseStudyDetails, deleteUploadedFile } from '../../../service/case-studies';
import { useParams } from 'react-router-dom';
import CustomToast from '../../Toast';
import CaseStudyHeader from '../../layouts/caseStudyHeader';
import { publishCaseStudies } from '../../../service/case-studies';
import apiFunctions from '../../../service/axiosConfig/apiFunctions';
import { baseUrl } from '../../../constants';

const EditCaseStudies = () => {
    const [title, setTitle] = useState('');
    const [subtitle, setSubtitle] = useState('');
    const [year, setYear] = useState();
    const [tags, setTags] = useState([]);
    const [content, setContent] = useState('');
    const [thumbnail, setThumbnail] = useState(null);
    const [previewUrl, setPreviewUrl] = useState();
    const [upload, setUpload] = useState(false);
    const [show, setShow] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadedUrl, setUploadedUrl] = useState([]);
    const [loader, setLoader] = useState(false);
    const { id } = useParams();
    const caseStudyId = id;
    const [file_path, setFile_path] = useState('');
    const [status, setStatus] = useState('draft');

    // MyUploadAdapter.js
    class MyUploadAdapter {
        constructor(loader, onFileUpload) {
            this.loader = loader;
            this.onFileUpload = onFileUpload;
        }
        upload() {
            return this.loader.file
                .then((file) => {
                    // Implement your file upload logic here
                    // For demonstration purposes, log the file name and size
                    // Call the function passed to the constructor
                    this.onFileUpload(file);

                    return new Promise((resolve, reject) => {
                        resolve({ file });
                    });
                })
                .catch((error) => {
                    console.error('Error uploading file:', error);
                    CustomToast.error('Error uploading file');
                    return Promise.reject('Error uploading file');
                });
        }
    }

    // Helper function to upload images
    const handleContentImageUpload = async (e, name, i, oi) => {
        if (e) {
            try {
                // Calling the new function to upload the rating
                const result = await apiFunctions.uploadImage(`${baseUrl}uploadImage`, e);
                await setFile_path(result.data.file)
                // Update the courseContent with the image URL
                if (name == "content") {
                    setContent((prevContent) => {
                        return prevContent.replace(/<figure class="image"><img><\/figure>/g, `<figure className="image"><img src="${result.data.file}"></figure>`);
                    });
                }

                return result.data.file_path;
            } catch (error) {
                console.error('Image upload error:', error);
                CustomToast.error('Error while uploading file');
            }

        }
        else {
            CustomToast.error('Please select an image');
            return;
        }

    };

    const handleAdditionalEditorChange = (data) => {
        setContent(data);
    };

    useEffect(() => {
        if (caseStudyId) {
            fetchCaseStudyDetails(caseStudyId);
        }
    }, [caseStudyId]);

    const fetchCaseStudyDetails = async (id) => {
        try {
            const res = await getCaseStudyDetails(id);
            const data = res?.data;

            setTitle(data.title || '');
            setSubtitle(data.subtitle || '');
            setYear(data.year || null);
            setTags(data.tags || []);
            setContent(data.case_study || '');
            setPreviewUrl(data.thumbnail || data.upload_url || []);
            setStatus(data.status || 'draft');
            setUploadedUrl(data.upload_url || []);
        } catch (error) {
            CustomToast.error("Failed to load case study.");
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setSelectedFile(file);
    };

    const handleImageUpload = async () => {
        if (!selectedFile) {
            CustomToast.error("Please select a file first.");
            return;
        }

        try {
            setLoader(true);
            const response = await apiFunctions.uploadDocFiles(`${baseUrl}uploadFiles`, selectedFile);
            const uploadedUrls = response.data?.urls;

            setUploadedUrl(uploadedUrls || []);
            setPreviewUrl(uploadedUrls?.[0] || null);

            CustomToast.success("Files uploaded successfully!");
        } catch (error) {
            console.error("Upload error:", error);
            CustomToast.error("Failed to upload files.");
        } finally {
            setLoader(false);
        }
    };

    const handleRemoveFile = () => {
        setUploadedUrl([]);
        setPreviewUrl(null);
        setSelectedFile(null);
        CustomToast.success("Image removed successfully!");
    };

    const handleSaveOrPublish = async (type) => {

        const payload = {
            title,
            subtitle,
            year: (year ? Number(year) : null),
            tags,
            case_study: content,
            upload_url: uploadedUrl ? (Array.isArray(uploadedUrl) ? uploadedUrl : [uploadedUrl]) : [],
            status: type
        };

        try {
            const response = await updateCaseStudies(id, payload);
            if (response?.data?.status) {
                setStatus(type);
                CustomToast.success("Case Study updated successfully");
            } else {
                console.error("Error updating Case Study:", response?.message);
                handleValidationMessages(response?.message);
            }

        } catch (err) {
            console.error(err);
            CustomToast.error("A network error occurred.");
        } finally {
            setIsSaving(false);
            setIsPublishing(false);
        }
    };

    const handleValidationMessages = (message) => {
        if (!message) {
            CustomToast.error('Unexpected error occurred.');
            return;
        }

        console.error("Validation Error:", message);

        if (message.includes('"title" is not allowed to be empty')) {
            CustomToast.error('Title is required.');
        } else if (message.includes('"subtitle" is not allowed to be empty')) {
            CustomToast.error('Subtitle is required.');
        } else if (message.includes('"case_study" is not allowed to be empty')) {
            CustomToast.error('Case Study content is required.');
        } else if (message.includes('"Year must be a number') || message.includes('"year" must be a number') || year === '' || year === null) {
            CustomToast.error('Year must be a number');
        } else if (message.includes('"At least one tag is required') || message.includes('"tags" is not allowed to be empty') || tags?.length === 0) {
            CustomToast.error('At least one tag is required');
        } else if (message.includes('At least one file must be uploaded')) {
            CustomToast.error('At least one file must be uploaded.');
        } else {
            CustomToast.error('Error occurred while saving the Case Study.');
        }
    };


    const handleTogglePublishStatus = async () => {
        setIsPublishing(true);

        try {
            // Toggle between published and draft
            const newStatus = status === 'published' ? 'draft' : 'published';
            const response = await handleUpdateCaseStudyStatus(caseStudyId, newStatus);

            if (response?.data?.status) {
                setStatus(newStatus);
                const actionMessage = newStatus === 'published' ? 'published' : 'moved to draft';
                CustomToast.success(`Case Study ${actionMessage} successfully`);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsPublishing(false);
        }
    };

    const handleUpdateCaseStudyStatus = async (caseStudyId, newStatus) => {
        const data = {
            is_publish: newStatus === 'published',
            publish_date: new Date().toISOString().split('T')[0],
            status: newStatus
        };

        try {
            const response = await publishCaseStudies(caseStudyId, data);
            return response;
        } catch (error) {
            CustomToast.error(`Error while updating Case Study status`);
            return error;
        }
    };


    return (
        <>
            <CaseStudyHeader
                isEdit={true}
                isSaving={isSaving}
                isPublishing={isPublishing}
                onSaveDraft={() => handleSaveOrPublish("draft")}
                onPublish={handleTogglePublishStatus}
                isSaveDisabled={false}
                currentStatus={status}
            />
            {/* Sidebar */}
            <aside className={`course_details_sidebar sidebar px-4 pt-5 ${show ? "review" : ""}`} id="admin-dash-nav">
                <div className='d-flex flex-column justify-content-between h-100 p-3 course_details'>
                    <div className='row'>
                        <h5 className="font-20 mb-4 fw-bold">Upload file</h5>
                        <label className="fw-bold mb-2">Upload Thumbnail</label>

                        <div
                            className="border rounded p-3 w-100 position-relative"
                            style={{
                                borderStyle: 'solid',
                                borderWidth: '1px',
                                borderColor: '#dee2e6',
                            }}
                        >
                            {!previewUrl && !selectedFile && !uploadedUrl?.length ? (
                                <div
                                    className="d-flex flex-column justify-content-center align-items-center w-100"
                                    style={{
                                        minHeight: '150px',
                                        backgroundColor: '#f8f9fa',
                                        border: '2px dashed #ccc',
                                        borderRadius: '8px',
                                        position: 'relative',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <img src="/images/File.svg" alt="file" style={{ width: '40px', marginBottom: '8px' }} />
                                    <p className="m-0 text-muted">Choose File</p>

                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        onClick={(e) => {
                                            e.target.value = null;
                                        }}
                                        style={{
                                            position: 'absolute',
                                            height: '100%',
                                            width: '100%',
                                            top: 0,
                                            left: 0,
                                            opacity: 0,
                                            cursor: 'pointer',
                                            zIndex: 5,
                                        }}
                                    />
                                </div>
                            ) : (
                                <div
                                    className="position-relative w-100 d-flex justify-content-center align-items-center"
                                    style={{
                                        minHeight: '150px',
                                        backgroundColor: '#f8f9fa',
                                        border: '2px dashed #ccc',
                                        borderRadius: '8px',
                                    }}
                                >
                                    <img
                                        src={uploadedUrl?.length > 0 ? uploadedUrl[0] : selectedFile ? URL.createObjectURL(selectedFile) : previewUrl}
                                        alt="Preview"
                                        style={{ maxHeight: '140px', maxWidth: '100%', objectFit: 'contain' }}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleRemoveFile}
                                        className="position-absolute btn btn-link p-0 m-0"
                                        style={{ top: '5px', right: '5px', zIndex: 10 }}
                                    >
                                        <img src="/images/delete.png" alt="remove" style={{
                                            width: '25px',
                                            backgroundColor: '#fff'
                                        }} />
                                    </button>
                                </div>
                            )}

                            {selectedFile && (
                                <>
                                    <p className="text-center mt-3 text-break" style={{ fontSize: '14px', color: '#333' }}>
                                        {selectedFile.name}
                                    </p>
                                    <div className="d-flex justify-content-center">
                                        <button
                                            type="button"
                                            className="btn btn-primary mt-1 px-5 w-75 py-2"
                                            onClick={handleImageUpload}
                                            disabled={loader}
                                        >
                                            {loader ? 'Uploading...' : 'Upload'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <div className='course_main'>
                <div className="container-fluid">
                    <form onSubmit={handleSaveOrPublish}>
                        {/* Title */}
                        <div className="mb-3">
                            <label className="form-label fw-bold">Case Study Title</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Enter title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                            />

                        </div>

                        {/* Subtitle */}
                        <div className="mb-3">
                            <label className="form-label fw-bold">Subtitle Title</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Enter subtitle"
                                value={subtitle}
                                onChange={(e) => setSubtitle(e.target.value)}
                            />

                        </div>

                        {/* Year */}
                        <div className="mb-3">
                            <label className="form-label fw-bold">Year</label>
                            <input
                                type="number"
                                className="form-control"
                                placeholder="Enter year"
                                value={year}
                                onChange={(e) => setYear(e.target.value)}
                                required
                            />

                        </div>

                        {/* Tag Input */}
                        <div className="mb-3">
                            <label className="form-label fw-bold">Add Tag</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Press Enter to add tag"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && e.target.value.trim() !== '') {
                                        e.preventDefault();
                                        setTags([...tags, e.target.value.trim()]);
                                        e.target.value = '';
                                    }
                                }}
                            // required
                            />

                            <div className="mt-2">
                                {tags?.map((tag, idx) => (
                                    <span key={idx} className="badge bg-secondary me-2 mb-2 d-inline-flex align-items-center">
                                        {tag}
                                        <button
                                            type="button"
                                            className="btn-close btn-close-white ms-2"
                                            style={{ fontSize: '0.525rem', color: '#fff', opacity: 1, boxShadow: 'none' }}
                                            onClick={() => {
                                                const updatedTags = tags.filter((_, index) => index !== idx);
                                                setTags(updatedTags);
                                            }}
                                            aria-label="Remove tag"
                                        ></button>
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Case Study CKEditor */}
                        <div className="mb-3">
                            <label className="form-label fw-bold">Case Study</label>
                            <CKEditor
                                editor={ClassicEditor}
                                data={content || ''}
                                name="content"
                                onReady={(editor) => {
                                    try {
                                        console.log("content", content)
                                        editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
                                            return new MyUploadAdapter(loader, (e) => handleContentImageUpload(e, "content"));
                                        };
                                    } catch (error) {
                                        // handleEditorError(error);
                                    }
                                }}
                                //onError={handleEditorError}
                                onChange={(event, editor) => handleAdditionalEditorChange(editor.getData())}
                            />
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default EditCaseStudies;

